import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { GlobalState } from "@/Reducers/interface";
import {
	Pipeline,
	OpportunityStage,
	DeletePipelineOrStageRequest,
	PipelineCreateRequest
} from "@/Contracts/Pipelines";

import {
	addNewPipeline,
	getPipelines,
	getPipelineDetails,
	reorderPipeline,
	deletePipelineOrStage,
	editPipelines
} from "../../Services/Pipeline";

export enum PipelinesActionTypes {
	SHOW_PIPELINES_LOADER = "SHOW_PIPELINES_LOADER",
	HIDE_PIPELINES_LOADER = "HIDE_PIPELINES_LOADER",
	PIPELINES_REQUESTED = "PIPELINES_REQUESTED",
	PIPELINES_SUCCESSFUL = "PIPELINES_SUCCESSFUL",
	PIPELINES_FAILED = "PIPELINES_FAILED",
	PIPELINE_DETAILS_REQUESTED = "PIPELINE_DETAILS_REQUESTED",
	PIPELINE_DETAILS_SUCCESSFUL = "PIPELINE_DETAILS_SUCCESSFUL",
	PIPELINE_DETAILS_FAILED = "PIPELINE_DETAILS_FAILED",
	PIPELINE_DETAILS_UPDATED = "PIPELINE_DETAILS_UPDATED",
	ADD_PIPELINE_REQUESTED = "ADD_PIPELINE_REQUESTED",
	ADD_PIPELINE_SUCCESSFUL = "ADD_PIPELINE_SUCCESSFUL",
	ADD_PIPELINE_FAILED = "ADD_PIPELINE_FAILED",
	EDIT_PIPELINE_REQUESTED = "EDIT_PIPELINE_REQUESTED",
	EDIT_PIPELINE_SUCCESSFUL = "EDIT_PIPELINE_SUCCESSFUL",
	EDIT_PIPELINE_FAILED = "EDIT_PIPELINE_FAILED",
	CONFIGURE_PIPELINE_DRAWER_OPENED = "CONFIGURE_PIPELINE_DRAWER_OPENED",
	CONFIGURE_PIPELINE_DRAWER_CLOSED = "CONFIGURE_PIPELINE_DRAWER_CLOSED",
	SET_CURRENT_PIPELINE = "SET_CURRENT_PIPELINE",
	DELETE_PIPELINE_OR_STAGE_REQUESTED = "DELETE_PIPELINE_OR_STAGE_REQUESTED",
	DELETE_PIPELINE_OR_STAGE_IN_PROGRESS = "DELETE_PIPELINE_OR_STAGE_IN_PROGRESS",
	DELETE_PIPELINE_OR_STAGE_FAILED = "DELETE_PIPELINE_OR_STAGE_FAILED"
}

export type ShowPipelinesLoaderAction =
	Action<PipelinesActionTypes.SHOW_PIPELINES_LOADER>;
export type HidePipelinesLoaderAction =
	Action<PipelinesActionTypes.HIDE_PIPELINES_LOADER>;
export type PipelinesRequestedAction =
	Action<PipelinesActionTypes.PIPELINES_REQUESTED>;
export interface PipelinesSuccessfulAction
	extends Action<PipelinesActionTypes.PIPELINES_SUCCESSFUL> {
	readonly pipelineList: Pipeline[];
}
export type PipelinesFailedAction =
	Action<PipelinesActionTypes.PIPELINES_FAILED>;
export type PipelineDetailsRequestedAction =
	Action<PipelinesActionTypes.PIPELINE_DETAILS_REQUESTED>;
export interface PipelineDetailsSuccessfulAction
	extends Action<PipelinesActionTypes.PIPELINE_DETAILS_SUCCESSFUL> {
	readonly currentActivePipeline: Pipeline;
}
export type PipelineDetailsFailedAction =
	Action<PipelinesActionTypes.PIPELINE_DETAILS_FAILED>;
export interface PipelineDetailsUpdatedAction
	extends Action<PipelinesActionTypes.PIPELINE_DETAILS_UPDATED> {
	readonly currentActivePipeline: Pipeline;
}
export type AddPipelineRequestedAction =
	Action<PipelinesActionTypes.ADD_PIPELINE_REQUESTED>;
export type AddPipelineSuccessfulAction =
	Action<PipelinesActionTypes.ADD_PIPELINE_SUCCESSFUL>;
export type AddPipelineFailedAction =
	Action<PipelinesActionTypes.ADD_PIPELINE_FAILED>;
export type EditPipelineRequestedAction =
	Action<PipelinesActionTypes.EDIT_PIPELINE_REQUESTED>;
export interface EditPipelineSuccessfulAction
	extends Action<PipelinesActionTypes.EDIT_PIPELINE_SUCCESSFUL> {
	readonly currentActivePipeline: Pipeline;
}
export type EditPipelineFailedAction =
	Action<PipelinesActionTypes.EDIT_PIPELINE_FAILED>;
export interface ConfigurePipelineDrawerOpenedAction
	extends Action<PipelinesActionTypes.CONFIGURE_PIPELINE_DRAWER_OPENED> {
	readonly isAddingPipeline: boolean;
	readonly isDeletingPipeline: boolean;
}
export type ConfigurePipelineDrawerClosedAction =
	Action<PipelinesActionTypes.CONFIGURE_PIPELINE_DRAWER_CLOSED>;
export interface SetActivePipeline
	extends Action<PipelinesActionTypes.SET_CURRENT_PIPELINE> {
	readonly currentPipeline: Pipeline;
}
export type DeletePipelineOrStageRequestedAction =
	Action<PipelinesActionTypes.DELETE_PIPELINE_OR_STAGE_REQUESTED>;
export type DeletePipelineOrStageInProgressAction =
	Action<PipelinesActionTypes.DELETE_PIPELINE_OR_STAGE_IN_PROGRESS>;
export type DeletePipelineOrStageFailedAction =
	Action<PipelinesActionTypes.DELETE_PIPELINE_OR_STAGE_FAILED>;

export type PipelinesActions =
	| ShowPipelinesLoaderAction
	| HidePipelinesLoaderAction
	| PipelinesRequestedAction
	| PipelinesSuccessfulAction
	| PipelinesFailedAction
	| PipelineDetailsUpdatedAction
	| PipelineDetailsRequestedAction
	| PipelineDetailsSuccessfulAction
	| PipelineDetailsFailedAction
	| AddPipelineRequestedAction
	| AddPipelineSuccessfulAction
	| AddPipelineFailedAction
	| EditPipelineRequestedAction
	| EditPipelineSuccessfulAction
	| EditPipelineFailedAction
	| ConfigurePipelineDrawerOpenedAction
	| ConfigurePipelineDrawerClosedAction
	| SetActivePipeline
	| DeletePipelineOrStageRequestedAction
	| DeletePipelineOrStageInProgressAction
	| DeletePipelineOrStageFailedAction;

export interface PipelinesDispatch {
	showPipelinesLoader: ActionCreator<
		ThunkAction<
			ShowPipelinesLoaderAction,
			GlobalState,
			null,
			ShowPipelinesLoaderAction
		>
	>;
	hidePipelinesLoader: ActionCreator<
		ThunkAction<
			HidePipelinesLoaderAction,
			GlobalState,
			null,
			HidePipelinesLoaderAction
		>
	>;
	getAllPipelines: ActionCreator<
		ThunkAction<
			Promise<PipelinesSuccessfulAction>,
			GlobalState,
			null,
			PipelinesSuccessfulAction
		>
	>;
	getPipelineDetails: ActionCreator<
		ThunkAction<
			Promise<PipelineDetailsSuccessfulAction>,
			GlobalState,
			string,
			PipelineDetailsSuccessfulAction
		>
	>;
	updatePipelineDetails: ActionCreator<
		ThunkAction<
			PipelineDetailsUpdatedAction,
			GlobalState,
			Pipeline,
			PipelineDetailsUpdatedAction
		>
	>;
	setActivePipeline: ActionCreator<
		ThunkAction<SetActivePipeline, GlobalState, Pipeline, SetActivePipeline>
	>;
	updateOpportunityStages: ActionCreator<
		ThunkAction<
			PipelineDetailsUpdatedAction,
			GlobalState,
			OpportunityStage[],
			PipelineDetailsUpdatedAction
		>
	>;
	addOpportunityStage: ActionCreator<
		ThunkAction<
			Promise<PipelineDetailsUpdatedAction>,
			GlobalState,
			string | undefined,
			PipelineDetailsUpdatedAction
		>
	>;
	deleteOpportunityStage: ActionCreator<
		ThunkAction<
			Promise<PipelineDetailsUpdatedAction>,
			GlobalState,
			{ selectedOpportunityStageId: string; movedStageTo: string },
			PipelineDetailsUpdatedAction
		>
	>;
	addPipeline: ActionCreator<
		ThunkAction<
			Promise<AddPipelineSuccessfulAction>,
			GlobalState,
			null,
			AddPipelineSuccessfulAction
		>
	>;
	editPipeline: ActionCreator<
		ThunkAction<
			Promise<EditPipelineSuccessfulAction>,
			GlobalState,
			Pipeline,
			EditPipelineSuccessfulAction
		>
	>;
	configurePipelineDrawerOpened: ActionCreator<
		ThunkAction<
			ConfigurePipelineDrawerOpenedAction,
			GlobalState,
			null,
			ConfigurePipelineDrawerOpenedAction
		>
	>;
	configurePipelineDrawerClosed: ActionCreator<
		ThunkAction<
			ConfigurePipelineDrawerClosedAction,
			GlobalState,
			null,
			ConfigurePipelineDrawerClosedAction
		>
	>;
	deletePipelineOrStage: ActionCreator<
		ThunkAction<
			Promise<DeletePipelineOrStageInProgressAction>,
			GlobalState,
			DeletePipelineOrStageRequest,
			DeletePipelineOrStageInProgressAction
		>
	>;
}

export const PipelinesActionCreators: PipelinesDispatch = {
	showPipelinesLoader: () => {
		return (dispatch: Dispatch): ShowPipelinesLoaderAction => {
			const showPipelinesLoaderAction: ShowPipelinesLoaderAction = {
				type: PipelinesActionTypes.SHOW_PIPELINES_LOADER
			};
			return dispatch(showPipelinesLoaderAction);
		};
	},
	hidePipelinesLoader: () => {
		return (dispatch: Dispatch): HidePipelinesLoaderAction => {
			const hidePipelinesLoaderAction: HidePipelinesLoaderAction = {
				type: PipelinesActionTypes.HIDE_PIPELINES_LOADER
			};
			return dispatch(hidePipelinesLoaderAction);
		};
	},
	getAllPipelines: () => {
		return async (
			dispatch: Dispatch
		): Promise<PipelinesSuccessfulAction> => {
			const pipelinesRequestedAction: PipelinesRequestedAction = {
				type: PipelinesActionTypes.PIPELINES_REQUESTED
			};
			dispatch(pipelinesRequestedAction);
			try {
				const pipelinesResponse = await getPipelines();
				const pipelinesSuccessfulAction: PipelinesSuccessfulAction = {
					type: PipelinesActionTypes.PIPELINES_SUCCESSFUL,
					pipelineList: pipelinesResponse.data
				};
				return dispatch(pipelinesSuccessfulAction);
			} catch (error) {
				const pipelinesFailedAction: PipelinesFailedAction = {
					type: PipelinesActionTypes.PIPELINES_FAILED
				};
				dispatch(pipelinesFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getPipelineDetails: (pipelineId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<PipelineDetailsSuccessfulAction> => {
			const pipelineDetailsRequestedAction: PipelineDetailsRequestedAction =
				{
					type: PipelinesActionTypes.PIPELINE_DETAILS_REQUESTED
				};
			dispatch(pipelineDetailsRequestedAction);
			try {
				const pipelineDetailsResponse = await getPipelineDetails(
					pipelineId
				);
				const pipelineDetailsSuccessfulAction: PipelineDetailsSuccessfulAction =
					{
						type: PipelinesActionTypes.PIPELINE_DETAILS_SUCCESSFUL,
						currentActivePipeline: pipelineDetailsResponse
					};
				return dispatch(pipelineDetailsSuccessfulAction);
			} catch (error) {
				const pipelineDetailsFailedAction: PipelineDetailsFailedAction =
					{
						type: PipelinesActionTypes.PIPELINE_DETAILS_FAILED
					};
				dispatch(pipelineDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updatePipelineDetails: (currentActivePipeline: Pipeline) => {
		return (dispatch: Dispatch): PipelineDetailsUpdatedAction => {
			const pipelineDetailsUpdatedAction: PipelineDetailsUpdatedAction = {
				type: PipelinesActionTypes.PIPELINE_DETAILS_UPDATED,
				currentActivePipeline
			};
			return dispatch(pipelineDetailsUpdatedAction);
		};
	},
	setActivePipeline: (currentActivePipeline: Pipeline) => {
		return (dispatch: Dispatch): SetActivePipeline => {
			const setPipelineAction: SetActivePipeline = {
				type: PipelinesActionTypes.SET_CURRENT_PIPELINE,
				currentPipeline: currentActivePipeline
			};
			return dispatch(setPipelineAction);
		};
	},
	updateOpportunityStages: (
		currentPipeline: Pipeline,
		opportunityStages: OpportunityStage[]
	) => {
		return (dispatch: Dispatch): PipelineDetailsUpdatedAction => {
			const currPipeline = currentPipeline;
			if (!currPipeline) {
				throw new Error("there is no current pipeline");
			}
			const pipelineDetailsUpdatedAction: PipelineDetailsUpdatedAction = {
				type: PipelinesActionTypes.PIPELINE_DETAILS_UPDATED,
				currentActivePipeline: {
					...currPipeline,
					opportunity_stages: opportunityStages
				}
			};
			reorderPipeline({ opportunityStages, pipelineId: currPipeline.id });
			return dispatch(pipelineDetailsUpdatedAction);
		};
	},
	addOpportunityStage: (
		currentPipeline: Pipeline,
		defaultOpportunityStageName: string | undefined
	) => {
		return async (
			dispatch: Dispatch
		): Promise<PipelineDetailsUpdatedAction> => {
			const currPipeline = currentPipeline;

			if (!currPipeline) {
				throw new Error("there is no current pipeline");
			}

			const newStage = {
				stage_name: defaultOpportunityStageName || "New Stage"
			};
			const newOpportunityStages: (
				| OpportunityStage
				| Omit<OpportunityStage, "id">
			)[] = [...currPipeline.opportunity_stages];
			newOpportunityStages.push(newStage);

			const pipelineResponse = await reorderPipeline({
				opportunityStages: newOpportunityStages,
				pipelineId: currPipeline.id
			});

			const pipelineDetailsUpdatedAction: PipelineDetailsUpdatedAction = {
				type: PipelinesActionTypes.PIPELINE_DETAILS_UPDATED,
				currentActivePipeline: pipelineResponse
			};

			return dispatch(pipelineDetailsUpdatedAction);
		};
	},
	// Delete Stage from a pipeline
	deleteOpportunityStage: ({
		currentPipeline,
		selectedOpportunityStageId,
		movedStageTo
	}: {
		currentPipeline: Pipeline;
		selectedOpportunityStageId: string;
		movedStageTo: string;
	}) => {
		return async (
			dispatch: Dispatch
		): Promise<PipelineDetailsUpdatedAction> => {
			const currPipeline = currentPipeline;

			if (!currPipeline) {
				throw new Error("there is no current pipeline");
			}

			const newOpportunityStages = currPipeline.opportunity_stages.map(
				currStage =>
					currStage.id === selectedOpportunityStageId
						? { ...currStage, move_to: movedStageTo }
						: currStage
			);

			const pipelineResponse = await reorderPipeline({
				opportunityStages: newOpportunityStages,
				pipelineId: currPipeline.id
			});

			const pipelineDetailsUpdatedAction: PipelineDetailsUpdatedAction = {
				type: PipelinesActionTypes.PIPELINE_DETAILS_UPDATED,
				currentActivePipeline: pipelineResponse
			};

			return dispatch(pipelineDetailsUpdatedAction);
		};
	},
	// Delete an existing pipeline or stage
	deletePipelineOrStage: (
		deletePipelineOrStageRequest: DeletePipelineOrStageRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<DeletePipelineOrStageInProgressAction> => {
			const deletePipelineRequestedAction: DeletePipelineOrStageRequestedAction =
				{
					type: PipelinesActionTypes.DELETE_PIPELINE_OR_STAGE_REQUESTED
				};
			dispatch(deletePipelineRequestedAction);
			try {
				await deletePipelineOrStage(deletePipelineOrStageRequest);
				const deletePipelineOrStageInProgressAction: DeletePipelineOrStageInProgressAction =
					{
						type: PipelinesActionTypes.DELETE_PIPELINE_OR_STAGE_IN_PROGRESS
					};
				return dispatch(deletePipelineOrStageInProgressAction);
			} catch (error) {
				const deletePipelineOrStageFailedAction: DeletePipelineOrStageFailedAction =
					{
						type: PipelinesActionTypes.DELETE_PIPELINE_OR_STAGE_FAILED
					};
				dispatch(deletePipelineOrStageFailedAction);

				return Promise.reject(error);
			}
		};
	},
	// Add a new Pipeline
	addPipeline: (addPipelineRequest: PipelineCreateRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddPipelineSuccessfulAction> => {
			const addPipelineRequestedAction: AddPipelineRequestedAction = {
				type: PipelinesActionTypes.ADD_PIPELINE_REQUESTED
			};
			dispatch(addPipelineRequestedAction);
			try {
				await addNewPipeline(addPipelineRequest);
				const addPipelineSuccessfulAction: AddPipelineSuccessfulAction =
					{
						type: PipelinesActionTypes.ADD_PIPELINE_SUCCESSFUL
					};
				return dispatch(addPipelineSuccessfulAction);
			} catch (error) {
				const addPipelineFailedAction: AddPipelineFailedAction = {
					type: PipelinesActionTypes.ADD_PIPELINE_FAILED
				};
				dispatch(addPipelineFailedAction);

				return Promise.reject(error);
			}
		};
	},
	// Edit an existing pipeline
	editPipeline: (pipelineId: string, editPipelineRequest: Pipeline) => {
		return async (
			dispatch: Dispatch
		): Promise<EditPipelineSuccessfulAction> => {
			const editPipelineRequestedAction: EditPipelineRequestedAction = {
				type: PipelinesActionTypes.EDIT_PIPELINE_REQUESTED
			};
			dispatch(editPipelineRequestedAction);
			try {
				const updatedPipeline = await editPipelines(
					pipelineId,
					editPipelineRequest
				);
				const editPipelineSuccessfulAction: EditPipelineSuccessfulAction =
					{
						type: PipelinesActionTypes.EDIT_PIPELINE_SUCCESSFUL,
						currentActivePipeline: updatedPipeline
					};
				return dispatch(editPipelineSuccessfulAction);
			} catch (error) {
				const editPipelineFailedAction: EditPipelineFailedAction = {
					type: PipelinesActionTypes.EDIT_PIPELINE_FAILED
				};
				dispatch(editPipelineFailedAction);

				return Promise.reject(error);
			}
		};
	},
	// Handle Pipeline Add/Edit Drawer
	configurePipelineDrawerOpened: (
		isAddingPipeline?: boolean,
		isDeletingPipeline?: boolean
	) => {
		return (dispatch: Dispatch): ConfigurePipelineDrawerOpenedAction => {
			const configurePipelineDrawerOpenedAction: ConfigurePipelineDrawerOpenedAction =
				{
					type: PipelinesActionTypes.CONFIGURE_PIPELINE_DRAWER_OPENED,
					isAddingPipeline: isAddingPipeline ? true : false,
					isDeletingPipeline: isDeletingPipeline ? true : false
				};
			return dispatch(configurePipelineDrawerOpenedAction);
		};
	},
	configurePipelineDrawerClosed: () => {
		return (dispatch: Dispatch): ConfigurePipelineDrawerClosedAction => {
			const configurePipelineDrawerClosedAction: ConfigurePipelineDrawerClosedAction =
				{
					type: PipelinesActionTypes.CONFIGURE_PIPELINE_DRAWER_CLOSED
				};
			return dispatch(configurePipelineDrawerClosedAction);
		};
	}
};

export default PipelinesActionCreators;
